import { clsx } from 'clsx';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, ReactNode } from 'react';
import { shallow } from 'zustand/shallow';

import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { Button } from 'src/general/components/Button/Button';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { CarDisplayState } from 'src/pdp/sections/Pdp/pdpHelpers';
import { authStore } from 'src/stores/authStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import { DataLayerPdpEvent, pushToDataLayerPDP } from 'src/utils/pushToDataLayerPDP';
import { carDataToDataLayerItem } from 'src/utils/pushToDataLayerProductCard';

import { PDP_PRICE_CARD_ID } from 'src/constants';
import { pdpStore } from 'src/stores/pdpStore';
import styles from './CarPriceCard.module.scss';
import { CarPriceCardDetails } from './CarPriceCardDetails';

interface Props {
  displayState: CarDisplayState;
  productDetails: ProductDetailsPayload;
  isCheckoutProgress: boolean;
  microcopy: {
    startCheckoutDefault: ReactNode;
    continueCheckout: ReactNode;
    notAvailable: ReactNode;
    carPurchased: ReactNode;
    purchaseInfo: ReactNode;
    financeData: ReactNode;
    govtCharges: ReactNode;
    footnote: ReactNode;
    carEnquiryContent: ReactNode;
  };
}

export const enum PRICE_CARD_IDS {
  PRICE = 'carCardPriceDescription',
  FINANCE = 'carCardFinanceDescription',
}

export const CarPriceCard: FC<Props> = ({ displayState, productDetails, isCheckoutProgress, microcopy }) => {
  const flags = useFlags();
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );
  const carData = productDetailPayloadToBaseCarData(productDetails);
  const { isUserLoggedIn } = authStore(
    (state) => ({
      isUserLoggedIn: state.isUserLoggedIn,
    }),
    shallow,
  );

  const startCheckout = flags?.experimentPdpCtaButton ?? microcopy.startCheckoutDefault;
  const href = isCheckoutProgress
    ? `/checkout?sku=${productDetails.sku}`
    : `/product-detail/${productDetails.sku}/get-started`;

  const onStartCheckout = () => {
    pushToDataLayer({
      event: 'add_to_cart',
      logged_in: isUserLoggedIn,
      ecommerce: {
        items: [carDataToDataLayerItem(carData)],
      },
    });
  };
  const onDisplayGovtCharges = () => {
    setCurrentModal({ name: 'governmentCharges' });
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'PRICE_GUIDE',
    });
  };
  const onOpenCarEnquiryForm = () => {
    setCurrentModal({ name: 'enquiry' });
    pushToDataLayer({
      event: 'lead_form_start',
      ecommerce: {
        items: [carDataToDataLayerItem(carData)],
      },
      entry_point: 'price_card',
    });
  };

  return (
    <aside id={PDP_PRICE_CARD_ID} className={styles.root}>
      <header className={styles.header}>
        <h5 className={clsx(styles.title, 'c-fw-bold', 'c-fs-h5')} data-testid="CarPriceCard-component-car-title">
          {carData.model}
        </h5>
        <h6
          className={clsx(styles.subtitle, 'c-fw-normal', 'c-fs-caption')}
          data-testid="CarPriceCard-component-car-subtitle"
        >
          {carData.variant}
        </h6>
        <CarPriceCardDetails carData={carData} />
      </header>
      {displayState === 'my-purchase' ? (
        <>
          <p
            className={clsx(styles.carPurchased, 'c-fw-bold', 'c-fs-h5')}
            data-testid="CarPriceCard-component-car-purchased"
          >
            {microcopy.carPurchased}
          </p>
          <Button href="/account/purchases" fullWidth data-testid="CarPriceCard-component-purchase-info">
            {microcopy.purchaseInfo}
          </Button>
        </>
      ) : (
        <>
          <h5
            className={clsx(styles.price, 'c-fw-bold', 'c-fs-h5')}
            title="Total price"
            aria-describedby={PRICE_CARD_IDS.PRICE}
            data-testid="CarPriceCard-component-price"
          >
            {formatNumber(carData.price, { prepend: '$', append: '*' })}
          </h5>
          <div
            className={clsx(styles.financePrice, 'c-fs-caption')}
            aria-describedby={PRICE_CARD_IDS.FINANCE}
            data-testid="CarPriceCard-component-finance-description"
          >
            {microcopy.financeData}
          </div>
          {(displayState === 'available' || displayState === 'continue-purchase' || displayState === 'loading') && (
            <Button
              prefetch={false}
              onClick={onStartCheckout}
              href={displayState === 'loading' ? undefined : href}
              isLoading={displayState === 'loading'}
              isDisabledWhileLoading
              fullWidth
              variant="accent"
              data-testid="CarPriceCard-component-get-started"
            >
              {displayState === 'continue-purchase' ? microcopy.continueCheckout : startCheckout}
            </Button>
          )}
          {displayState === 'not-available' && (
            <Button
              href={`/product-detail/${carData.sku}/similar`}
              fullWidth
              data-testid="CarPriceCard-component-view-similars"
            >
              {microcopy.notAvailable}
            </Button>
          )}
          <div className={styles.enquiryBlock}>
            <Button
              fullWidth
              variant="secondary"
              size="small"
              onClick={onOpenCarEnquiryForm}
              data-testid="CAR_ENQUIRY_BLOCK_CONTENT_BUTTON"
            >
              {microcopy.carEnquiryContent}
            </Button>
          </div>
          <footer className={styles.footer}>
            <Button
              variant="link"
              size="small"
              onClick={onDisplayGovtCharges}
              id={PRICE_CARD_IDS.PRICE}
              data-testid={PRICE_CARD_IDS.PRICE}
              className={styles.priceFootnote}
            >
              *{microcopy.govtCharges}
            </Button>
            <p className={clsx(styles.financeFootnote, 'c-fw-normal', 'c-fs-caption')} id={PRICE_CARD_IDS.FINANCE}>
              {microcopy.footnote}
            </p>
          </footer>
        </>
      )}
    </aside>
  );
};
